import { useDebouncedValue } from "@mantine/hooks"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useState } from "react"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { SearchUnavailableError, searchMeetings } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { useProduct } from "../../lib/tenant"
import { isSearchChannel, type TSearchChannel } from "../../lib/types/search"
import type { TSearchSort } from "./-search/present-hits"
import { SearchView } from "./-search/SearchView"
import type { TSearchViewState } from "./-search/SearchView/interfaces"

interface ISearchRouteSearch {
  q?: string
  channels?: TSearchChannel[]
  speaker?: string
  from?: string
  to?: string
}

/** Minimum characters before we fire a search — a 1-char term is all noise. */
const MIN_QUERY_LENGTH = 2
/** Warming poll cadence, and the soft deadline after which the copy softens. */
const WARMING_POLL_MS = 700
const WARMING_LONG_MS = 8000

export const Route = createFileRoute("/_auth/search")({
  component: SearchPage,
  validateSearch: (search: Record<string, unknown>): ISearchRouteSearch => {
    const out: ISearchRouteSearch = {}
    if (typeof search.q === "string" && search.q.length > 0) out.q = search.q
    if (typeof search.speaker === "string" && search.speaker.length > 0) out.speaker = search.speaker
    if (typeof search.from === "string" && search.from.length > 0) out.from = search.from
    if (typeof search.to === "string" && search.to.length > 0) out.to = search.to

    const raw = search.channels
    const tokens = Array.isArray(raw)
      ? raw.filter((c): c is string => typeof c === "string")
      : typeof raw === "string"
        ? raw.split(",")
        : []
    const channels = tokens.filter(isSearchChannel)
    if (channels.length > 0) out.channels = Array.from(new Set(channels))
    return out
  },
})

function SearchPage(): React.JSX.Element {
  const company = useCompanyApi()
  const product = useProduct()
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()

  // Text inputs are local typing buffers; their debounced values sync into the
  // URL, which is the single source of truth the query reads from. This keeps
  // the search shareable/bookmarkable without laggy per-keystroke navigation.
  const [queryInput, setQueryInput] = useState(search.q ?? "")
  const [debouncedQuery] = useDebouncedValue(queryInput, 300)
  const [speakerInput, setSpeakerInput] = useState(search.speaker ?? "")
  const [sort, setSort] = useState<TSearchSort>("relevance")
  const [debouncedSpeaker] = useDebouncedValue(speakerInput, 300)

  useEffect(() => {
    const next = debouncedQuery.trim()
    if (next === (search.q ?? "")) return
    navigate({ search: (prev) => ({ ...prev, q: next.length > 0 ? next : undefined }), replace: true })
  }, [debouncedQuery, search.q, navigate])

  useEffect(() => {
    const next = debouncedSpeaker.trim()
    if (next === (search.speaker ?? "")) return
    navigate({ search: (prev) => ({ ...prev, speaker: next.length > 0 ? next : undefined }), replace: true })
  }, [debouncedSpeaker, search.speaker, navigate])

  const q = (search.q ?? "").trim()
  const enabled = !!company && q.length >= MIN_QUERY_LENGTH

  const queryResult = useCompanyQuery({
    key: (orgId) => queryKeys.search(orgId, q, search.channels, search.speaker, search.from, search.to),
    queryFn: (client) =>
      searchMeetings(client, {
        q,
        channels: search.channels,
        speaker: search.speaker,
        from: search.from,
        to: search.to,
      }),
    enabled,
    // A 503 / error shouldn't hammer the engine; "warming" is a 200 status
    // (handled by refetchInterval below), not a thrown error.
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === "warming" ? WARMING_POLL_MS : false),
  })

  // Soften the warming copy once it runs past the deadline. The timer is armed
  // when status first becomes "warming" and cleared when it resolves.
  const status = queryResult.data?.status
  const [warmingLong, setWarmingLong] = useState(false)
  useEffect(() => {
    if (status !== "warming") {
      setWarmingLong(false)
      return
    }
    const timer = setTimeout(() => setWarmingLong(true), WARMING_LONG_MS)
    return () => clearTimeout(timer)
  }, [status])

  const viewState: TSearchViewState = ((): TSearchViewState => {
    if (!enabled) return "prequery"
    if (queryResult.error) return queryResult.error instanceof SearchUnavailableError ? "unavailable" : "error"
    if (queryResult.data?.status === "warming") return warmingLong ? "warming-long" : "warming"
    if (queryResult.data) return "results"
    return "loading"
  })()

  const toggleChannel = useCallback(
    (channel: TSearchChannel): void => {
      navigate({
        search: (prev) => {
          const current = prev.channels ?? []
          const next = current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel]
          return { ...prev, channels: next.length > 0 ? next : undefined }
        },
        replace: true,
      })
    },
    [navigate],
  )

  const setFrom = useCallback(
    (value: string): void => {
      navigate({ search: (prev) => ({ ...prev, from: value.length > 0 ? value : undefined }), replace: true })
    },
    [navigate],
  )

  const setTo = useCallback(
    (value: string): void => {
      navigate({ search: (prev) => ({ ...prev, to: value.length > 0 ? value : undefined }), replace: true })
    },
    [navigate],
  )

  const clearAll = useCallback((): void => {
    setSpeakerInput("")
    navigate({ search: (prev) => ({ q: prev.q }), replace: true })
  }, [navigate])

  const hasActiveFilters = (search.channels?.length ?? 0) > 0 || speakerInput.length > 0 || !!search.from || !!search.to

  return (
    <SearchView
      product={product}
      query={queryInput}
      onQueryChange={setQueryInput}
      filters={{
        channels: search.channels ?? [],
        onToggleChannel: toggleChannel,
        speaker: speakerInput,
        onSpeakerChange: setSpeakerInput,
        from: search.from ?? "",
        to: search.to ?? "",
        onFromChange: setFrom,
        onToChange: setTo,
        onClearAll: clearAll,
        hasActiveFilters,
      }}
      viewState={viewState}
      hits={queryResult.data?.hits ?? []}
      sort={sort}
      onSortChange={setSort}
      engine={queryResult.data?.engine ?? null}
      errorMessage={queryResult.error?.message}
      onRetry={() => {
        void queryResult.refetch()
      }}
    />
  )
}
